/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */
class UserStoreProvider {
  private dbName = '__phenix_store__';
  private storeName = 'user';
  private version = 1;
  private userStore;
  private db;

  async initialize(): Promise<void> {
    return new Promise((resolve, reject): void => {
      const openRequest = indexedDB.open(this.dbName, this.version);

      openRequest.onupgradeneeded = (): void => {
        this.db = openRequest.result;

        if (!this.db.objectStoreNames.contains(this.storeName)) {
          this.db.createObjectStore(this.storeName);
        }
      };

      openRequest.onsuccess = (): void => {
        resolve();
        this.db = openRequest.result;
      };

      openRequest.onerror = (): void => {
        reject();
      };
    });
  }

  async openConnection(): Promise<void> {
    if (!this.db) {
      await this.initialize();
    }

    const transaction = this.db.transaction(this.storeName, 'readwrite');

    this.userStore = transaction.objectStore(this.storeName);
  }

  static browserSupported(): boolean {
    return 'indexedDB' in window;
  }

  async get(key: string, defaultValue: string): Promise<string> {
    if (!this.db) {
      await this.initialize();
    }

    const transaction = this.db.transaction(this.storeName, 'readwrite');

    this.userStore = transaction.objectStore(this.storeName);

    return new Promise((resolve): void => {
      const request = this.userStore.get(key);

      request.onsuccess = (): void => {
        resolve(request.result !== undefined ? request.result[key] : defaultValue);
      };
      request.onerror = (): void => {
        resolve(defaultValue);
      };
    });
  }

  async set(key: string, value: string): Promise<boolean> {
    try {
      const response = await this.get(key, null);

      if (response === null) {
        await this.create(key, value);
      } else {
        await this.update(key, value);
      }

      return Promise.resolve(true);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  private async create(key: string, value: string): Promise<void> {
    await this.openConnection();

    return new Promise((resolve, reject): void => {
      const data = {[key]: value};
      const request = this.userStore.add(data, key);

      request.onsuccess = (): void => {
        resolve(request.result);
      };
      request.onerror = (): void => {
        reject(request.error);
      };
    });
  }

  private async update(key: string, value: string): Promise<boolean> {
    await this.openConnection();

    return new Promise((resolve, reject): void => {
      const data = {[key]: value};
      const request = this.userStore.put(data, key);

      request.onsuccess = (): void => {
        resolve(true);
      };
      request.onerror = (): void => {
        reject(this.userStore.error);
      };
    });
  }
}

export default UserStoreProvider;